import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { BehaviorSubject, Subscription } from 'rxjs';
import { HttpsService } from './https.service';
import { UserService } from './user.service';
import { isCacheFresh } from '../utils/cache-policy';

export type AccessRole = 'administrador' | 'equipe' | 'cliente';

export interface AuthorizationContext {
  userId: number;
  role: AccessRole;
  adminUserId: number;
  clientId: number;
  permissionsRevision: number;
  appPermissions: string[];
  workPermissions: Record<string, string[]>;
  accessExpiresAt: number | null;
  cachedAt: number;
}

@Injectable({ providedIn: 'root' })
export class AuthorizationService {
  private readonly cachePrefix = 'authorization_context_v6_';
  private readonly cacheIndexPrefix = 'authorization_context_index_v6_';
  private readonly backgroundRefreshAfterMs = 2 * 60 * 1000;
  private readonly backgroundRefreshCooldownMs = 30 * 1000;
  private readonly contextSubject = new BehaviorSubject<AuthorizationContext | null>(null);
  readonly context$ = this.contextSubject.asObservable();
  private storageReady: Promise<Storage | null> | null = null;
  private pendingRefresh: Promise<AuthorizationContext> | null = null;
  private lastBackgroundRefreshAt = 0;
  private userSubscription: Subscription;

  constructor(
    private storage: Storage,
    private https: HttpsService,
    private userService: UserService,
    private alertCtrl: AlertController
  ) {
    window.addEventListener('authorization-invalid', () => void this.invalidate());
    window.addEventListener('authorization-refresh-needed', () => this.refreshInBackground(true));
    window.addEventListener('focus', () => this.refreshInBackground(true));
    this.userSubscription = this.userService.user$.subscribe((user) => {
      const atual = this.contextSubject.value;
      if (!user && atual) {
        void this.invalidate();
      } else if (user && atual && Number(user.user_id) !== atual.userId) {
        void this.invalidate();
      }
    });
  }

  getContext(): AuthorizationContext | null {
    return this.contextSubject.value;
  }

  async load(): Promise<AuthorizationContext> {
    const user = this.userService.user || await this.userService.loadUser();
    const userId = Number(user?.user_id || 0);
    if (!userId) {
      return this.emptyContext();
    }

    const current = this.contextSubject.value;
    if (current?.userId === userId && isCacheFresh(current.cachedAt)) {
      this.refreshInBackground();
      return current;
    }

    const indexedKey = await this.cacheGet(this.cacheIndexPrefix + userId);
    const cached = indexedKey ? await this.cacheGet(indexedKey) : null;
    if (this.isValidContext(cached, userId)) {
      this.contextSubject.next(cached);
      this.refreshInBackground();
      return cached;
    }

    // Mantém o último escopo conhecido durante a atualização. Se o aparelho
    // estiver offline, esse contexto preserva somente as obras já autorizadas.
    if (this.isCompatibleCachedContext(cached, userId)) {
      this.contextSubject.next(cached);
    }

    return this.refresh();
  }

  async refresh(force = false): Promise<AuthorizationContext> {
    const user = this.userService.user || await this.userService.loadUser();
    const userId = Number(user?.user_id || 0);
    const current = this.contextSubject.value;
    if (!force && this.isValidContext(current, userId)) {
      return current;
    }

    if (this.pendingRefresh) {
      return this.pendingRefresh;
    }
    this.pendingRefresh = this.fetchContext();
    try {
      return await this.pendingRefresh;
    } finally {
      this.pendingRefresh = null;
    }
  }

  can(permission: string, obraId?: number | string | null): boolean {
    const context = this.contextSubject.value;
    if (!context) return false;
    if (context.role === 'administrador') return true;
    if (!this.accessWithinValidity(context)) return false;

    const variants = this.permissionVariants(permission);
    if (obraId !== undefined && obraId !== null && Number(obraId) > 0) {
      const permissions = context.workPermissions[String(Number(obraId))] || [];
      return permissions.includes('*')
        || permissions.some((item) => variants.includes(this.normalize(item)));
    }
    return context.appPermissions.includes('*')
      || context.appPermissions.some((item) => variants.includes(this.normalize(item)));
  }

  canAny(permissions: string[], obraId?: number | string | null): boolean {
    return permissions.some((permission) => this.can(permission, obraId));
  }

  canAll(permissions: string[], obraId?: number | string | null): boolean {
    return permissions.every((permission) => this.can(permission, obraId));
  }

  canInAnyWork(permission: string): boolean {
    const context = this.contextSubject.value;
    if (!context) return false;
    if (context.role === 'administrador') return true;
    if (!this.accessWithinValidity(context)) return false;

    const variants = this.permissionVariants(permission);
    return Object.values(context.workPermissions).some((permissions) =>
      permissions.includes('*')
      || permissions.some((item) => variants.includes(this.normalize(item)))
    );
  }

  async require(permission: string, obraId?: number | string | null): Promise<boolean> {
    await this.load();
    if (this.can(permission, obraId)) return true;

    const alert = await this.alertCtrl.create({
      header: 'Acesso não permitido',
      message: 'Você não possui permissão para esta ação.',
      cssClass: ['custom-alert', 'alert-confirm'],
      buttons: [{ text: 'Entendi', role: 'cancel' }]
    });
    await alert.present();
    return false;
  }

  async invalidate(): Promise<void> {
    const current = this.contextSubject.value;
    this.contextSubject.next(null);
    this.pendingRefresh = null;
    this.lastBackgroundRefreshAt = 0;
    if (current?.userId) {
      const indexKey = this.cacheIndexPrefix + current.userId;
      const contextKey = await this.cacheGet(indexKey);
      if (contextKey) await this.cacheRemove(contextKey);
      await this.cacheRemove(indexKey);
    }
  }

  private async fetchContext(): Promise<AuthorizationContext> {
    const user = this.userService.user || await this.userService.loadUser();
    const userId = Number(user?.user_id || 0);
    if (!userId) return this.emptyContext();

    try {
      const formData = new FormData();
      formData.append('data', JSON.stringify({ callback: 'contexto' }));
      const response: any = await this.https.post(formData, 'autorizacao.php');
      if (!response?.success || !response?.result) {
        throw new Error(response?.msg || 'Contexto de autorização indisponível.');
      }
      const context = this.mapContext(response.result);
      const previousKey = await this.cacheGet(this.cacheIndexPrefix + userId);
      const nextKey = this.cacheKey(userId, context.permissionsRevision);
      if (previousKey && previousKey !== nextKey) await this.cacheRemove(previousKey);
      await this.cacheSet(nextKey, context);
      await this.cacheSet(this.cacheIndexPrefix + userId, nextKey);
      this.contextSubject.next(context);
      return context;
    } catch (error) {
      const current = this.contextSubject.value;
      if (current?.userId === userId) return current;

      const fallback = this.contextFromUser(user);
      this.contextSubject.next(fallback);
      return fallback;
    }
  }

  private refreshInBackground(urgent = false): void {
    const context = this.contextSubject.value;
    if (!context?.userId) return;
    if (!urgent && isCacheFresh(context.cachedAt, this.backgroundRefreshAfterMs)) return;

    const now = Date.now();
    if (!urgent && now - this.lastBackgroundRefreshAt < this.backgroundRefreshCooldownMs) return;
    this.lastBackgroundRefreshAt = now;
    void this.refresh(true);
  }

  private contextFromUser(user: any): AuthorizationContext {
    const isClient = !!user?.is_client_user;
    const isMember = !!user?.is_team_member;
    const role: AccessRole = isClient ? 'cliente' : (isMember ? 'equipe' : 'administrador');
    return {
      userId: Number(user?.user_id || 0),
      role,
      adminUserId: Number(user?.access_admin_user_id || user?.team_admin_user_id || user?.user_id || 0),
      clientId: Number(user?.client_id || 0),
      permissionsRevision: Number(user?.permissions_revision || 1),
      appPermissions: role === 'administrador' ? ['*'] : (Array.isArray(user?.permissoes_app) ? user.permissoes_app.map((item: any) => this.normalize(String(item))) : []),
      workPermissions: {},
      accessExpiresAt: this.mapExpiration(user?.access_expires_at),
      cachedAt: Date.now()
    };
  }

  private mapContext(raw: any): AuthorizationContext {
    const works: Record<string, string[]> = {};
    Object.entries(raw.work_permissions || {}).forEach(([obraId, permissions]) => {
      works[String(Number(obraId))] = Array.isArray(permissions)
        ? permissions.map((item) => this.normalize(String(item))) : [];
    });
    return {
      userId: Number(raw.user_id || 0),
      role: ['administrador', 'equipe', 'cliente'].includes(raw.role) ? raw.role : 'equipe',
      adminUserId: Number(raw.admin_user_id || 0),
      clientId: Number(raw.client_id || 0),
      permissionsRevision: Number(raw.permissions_revision || 1),
      appPermissions: Array.isArray(raw.app_permissions) ? raw.app_permissions.map((item: any) => this.normalize(String(item))) : [],
      workPermissions: works,
      accessExpiresAt: this.mapExpiration(raw.access_expires_at),
      cachedAt: Date.now()
    };
  }

  private normalize(permission: string): string {
    const aliases: Record<string, string> = {
      'obra.visualizar': 'obras.visualizar',
      'obra.editar': 'obras.editar',
      'obras.finalizar': 'obras.editar',
      'pedidos.visualizar': 'listas.visualizar',
      'pedidos.criar': 'listas.criar',
      'pedidos.editar': 'listas.editar',
      'pedidos.excluir': 'listas.excluir',
      'pedidos.compartilhar': 'listas.compartilhar',
      'anotacoes.visualizar': 'linha_tempo.visualizar',
      'anotacoes.criar': 'linha_tempo.criar',
      'anotacoes.editar': 'linha_tempo.editar',
      'anotacoes.excluir': 'linha_tempo.excluir',
      'relatorio_fotografico.compartilhar': 'relatorio_fotografico.gerar'
    };
    const key = String(permission || '').trim().toLowerCase();
    return aliases[key] || key;
  }

  private permissionVariants(permission: string): string[] {
    const normalized = this.normalize(permission);
    if (['documentos.enviar', 'documentos.baixar', 'documentos.compartilhar'].includes(normalized)) {
      return [normalized, 'documentos.arquivos'];
    }
    return [normalized];
  }

  private async getStorage(): Promise<Storage | null> {
    if (!this.storageReady) {
      this.storageReady = this.storage.create().catch((error) => {
        
        return null;
      });
    }
    return this.storageReady;
  }

  private async cacheGet(key: string): Promise<any> {
    try {
      const storage = await this.getStorage();
      if (storage) return await storage.get(key);
    } catch { /* Usa o fallback abaixo. */ }
    if (typeof localStorage === 'undefined') return null;
    try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
  }

  private async cacheSet(key: string, value: any): Promise<void> {
    try {
      const storage = await this.getStorage();
      if (storage) {
        await storage.set(key, value);
        return;
      }
    } catch { /* Usa o fallback abaixo. */ }
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, JSON.stringify(value));
  }

  private async cacheRemove(key: string): Promise<void> {
    try {
      const storage = await this.getStorage();
      if (storage) await storage.remove(key);
    } catch { /* Remove também do fallback. */ }
    if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
  }

  private cacheKey(userId: number, revision: number): string {
    return `${this.cachePrefix}${userId}_${Math.max(1, revision)}`;
  }

  private isValidContext(value: any, userId: number): value is AuthorizationContext {
    return this.isCompatibleCachedContext(value, userId) && this.accessWithinValidity(value) && isCacheFresh(value.cachedAt);
  }

  private isCompatibleCachedContext(value: any, userId: number): value is AuthorizationContext {
    return !!value && Number(value.userId) === userId
      && ['administrador', 'equipe', 'cliente'].includes(value.role)
      && Number(value.adminUserId || 0) > 0
      && Number(value.permissionsRevision || 0) > 0
      && Array.isArray(value.appPermissions)
      && !!value.workPermissions
      && typeof value.workPermissions === 'object'
      && (value.accessExpiresAt === null || Number(value.accessExpiresAt) > 0)
      && Number(value.cachedAt || 0) > 0;
  }

  private accessWithinValidity(context: AuthorizationContext): boolean {
    return context.role === 'administrador'
      || context.accessExpiresAt === null
      || context.accessExpiresAt >= Date.now();
  }

  private mapExpiration(value: any): number | null {
    const texto = String(value || '').trim();
    if (!texto) return null;
    const timestamp = new Date(texto.replace(' ', 'T')).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  private emptyContext(): AuthorizationContext {
    return { userId: 0, role: 'equipe', adminUserId: 0, clientId: 0, permissionsRevision: 0, appPermissions: [], workPermissions: {}, accessExpiresAt: null, cachedAt: Date.now() };
  }
}
